import { Auth } from 'aws-amplify';
import { useState, useCallback, useEffect } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';

import { setAccessToken, setCognitoUser } from '../../state/auth/actions';
import useValidationMessages from '../../helpers/useValidationMessages';
import View from './View';

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [showErrors, setShowErrors] = useState(false);
  const [username, setUsername] = useState();

  const { invalidEmailAddress, required } = useValidationMessages();

  const accessToken = useSelector((state) => state.auth.accessToken);

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(invalidEmailAddress).required(required),
    password: Yup.string().required(required),
  });

  const handleSignIn = useCallback((params) => {
    setError(undefined);
    setLoading(true);
    setShowErrors(true);

    requestAnimationFrame(() => {
      signIn(params);
    });
  }, []);

  useEffect(() => {
    if (accessToken) {
      navigate('/');
    }
  }, [accessToken, navigate]);

  async function signIn(params) {
    const { email: username, password } = params;

    setUsername(username);

    try {
      const user = await Auth.signIn(username, password);
      dispatch(setAccessToken(user.signInUserSession.accessToken.jwtToken));
      dispatch(setCognitoUser(user));

      client.cache.evict({ fieldName: 'getBook' });
      client.cache.evict({ fieldName: 'listBooks' });
    } catch (error) {
      console.log('error signing in', error);

      // Handles migration of users from old system
      if (error.code === 'PasswordResetRequiredException') {
        navigate('/reset-password', {
          state: { email: username, fromLogin: true },
        });
      } else {
        setError(error.code || 'UnknownError');
        setLoading(false);
      }
    }
  }

  return (
    <View
      handleSignIn={handleSignIn}
      isLoading={isLoading}
      error={error}
      initialValues={initialValues}
      validationSchema={validationSchema}
      showErrors={showErrors}
      username={username}
    />
  );
};

export default SignIn;
